import { useState, useEffect, useCallback } from 'react';
import { Trophy, RefreshCw, Medal, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrophyRibbon from './TrophyRibbon';
import useCompetitionStatus from '../hooks/useCompetitionStatus';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getRankDisplay(rank) {
  switch (rank) {
    case 1:
      return <Medal className="w-6 h-6 text-[#FFD700]" />;
    case 2:
      return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    case 3:
      return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    default:
      return <span className="text-lg font-bold text-gray-400">{rank}</span>;
  }
}

function getRankBg(rank) {
  switch (rank) {
    case 1: return 'bg-yellow-50 border-yellow-200';
    case 2: return 'bg-gray-50 border-gray-200';
    case 3: return 'bg-orange-50 border-orange-200';
    default: return 'bg-white border-gray-100';
  }
}

export default function CombinedLeaderboard({ currentUserId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const compStatus = useCompetitionStatus();

  // Which sport columns to render — everything the app supports minus
  // whatever the backend flags as closed. Order preserved.
  const SPORT_COLS = [
    { key: 'afl', label: 'AFL', sport: 'AFL', color: '#E00000' },
    { key: 'nrl', label: 'NRL', sport: 'NRL', color: '#007A33' },
    { key: 'epl', label: 'EPL', sport: 'EPL', color: '#37003C' },
    { key: 'al',  label: 'AL',  sport: 'AL',  color: '#F0423E' },
  ];
  const closedSet = new Set(compStatus?.closed_sports || []);
  const visibleSports = SPORT_COLS.filter(s => !closedSet.has(s.sport));
  const nCols = visibleSports.length;
  // Grid template — one column per active sport + rank/name/total.
  const gridCols = `3rem 1fr ${'3.5rem '.repeat(nCols).trim()} 4rem`;

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leaderboard/combined/all`, { withCredentials: true });
      setLeaderboard(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 60000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  return (
    <div className="tipster-card" data-testid="combined-leaderboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-[#FFD700]" />
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1A1A1A]">Leaderboard</h3>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-400 hidden sm:block">
              {lastUpdated.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-[#007AFF] hover:bg-blue-50"
            onClick={fetchLeaderboard}
            data-testid="refresh-leaderboard-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Table Header */}
      <div
        className="grid gap-2 px-3 py-2 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className="text-center">#</div>
        <div>Tipster</div>
        {visibleSports.map(s => (
          <div key={s.key} className="text-center">{s.label}</div>
        ))}
        <div className="text-center">Total</div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Trophy className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm">No tips submitted yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {leaderboard.map((entry) => {
            const isMe = entry.user_id === currentUserId;
            const totalPts = ((Math.round(((entry.total_points) || 0) * 100) / 100)).toFixed(2);
            return (
              <div
                key={entry.user_id}
                className={`grid gap-2 items-center px-3 py-3 transition-colors ${
                  getRankBg(entry.rank)
                } ${isMe ? 'ring-1 ring-[#007AFF]/30 rounded-sm' : ''}`}
                style={{ gridTemplateColumns: gridCols }}
                data-testid={`leaderboard-entry-${entry.name.toLowerCase()}`}
              >
                {/* Rank */}
                <div className="flex items-center justify-center">
                  {getRankDisplay(entry.rank)}
                </div>

                {/* Name */}
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#007AFF] to-[#3395FF] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {entry.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className={`font-semibold text-sm flex items-center gap-1.5 ${isMe ? 'text-[#007AFF]' : 'text-[#1A1A1A]'}`}>
                      <span className="truncate">{entry.name}</span>
                      <TrophyRibbon titles={entry.titles} />
                      {isMe && <span className="text-xs text-[#007AFF] flex-shrink-0">(You)</span>}
                    </div>
                    <div className="text-xs text-gray-400" title="Correct picks out of completed games">
                      {entry.correct_tips}/{entry.total_tips} of completed
                    </div>
                  </div>
                </div>

                {/* Sport columns (dynamic — closed sports omitted) */}
                {visibleSports.map(s => {
                  const pts = ((Math.round(((entry[s.key]?.points) || 0) * 100) / 100)).toFixed(2);
                  const wins = entry[s.key]?.correct || 0;
                  return (
                    <div key={s.key} className="text-center">
                      <div className="text-sm font-bold" style={{ color: s.color }}>{pts}</div>
                      <div className="text-xs text-gray-400">{wins}W</div>
                    </div>
                  );
                })}

                {/* Total Points */}
                <div className="text-center">
                  <div className="text-xl font-black text-[#1A1A1A]">{totalPts}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
